<script setup>
import { ref, computed, watch } from 'vue'
import { useAppStore } from '@/stores/app'
import { useDataStore } from '@/stores/data'
import { useMapStore } from '@/stores/map'
import { setSheetHeight, BottomSheetStates } from '@/utils/eventBus'
import { smoothScroll } from '@/utils/scroll'
import { ElMessageBox, ElSelect } from 'element-plus'
import { autoCompleteSurveyForJourneyTrip } from '@/utils/journeyUtils'
import 'element-plus/es/components/select/style/css'
import 'element-plus/es/components/message-box/style/css'
import BasePage from './BasePage.vue'
import ButtonMenu from '@/components/shared/ButtonMenu.vue'
import TripSurveyQuestion from '@/components/shared/TripSurveyQuestion.vue'
import DefaultHeader from '@/components/shared/DefaultHeader.vue'
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue'

const appStore = useAppStore()
const mapStore = useMapStore()
const dataStore = useDataStore()
const splitIndex = ref(-1)

const currentJourneys = computed(() => dataStore.currentJourneys)
const currentJourney = computed(() => dataStore.currentJourney)
const selectedTrip = ref(null)
const splitTripToJourney = ref(false)
const includeNextJourneyTrips = ref(false)

const canApproveCurrentJourney = computed(() => {
	const currentIndex = dataStore.currentJourneyIndex
	if (currentIndex <= 0) return true
	const prevJourney = dataStore.currentJourneys?.[currentIndex - 1]
	return !!prevJourney?.approved
})

const pageClasses = computed(() => ({
	//active: appStore.currentPage === 'trips',
	//previous: appStore.previousPage === 'trips',
	edit: appStore.editStep === 'change',
	join: appStore.editStep === 'joinTrips',
	split: appStore.editStep === 'splitTrip',
	select: appStore.editStep === 'addTrip',
	delete: appStore.editStep === 'deleteTrip',
}))

const toggleIncludeNextJourneyTrips = () => {
	includeNextJourneyTrips.value = !includeNextJourneyTrips.value
	if (!includeNextJourneyTrips.value) resetNextJourneyJoinFlags()
	mapStore.plotTripsInner(dataStore.getTripsForJoin(includeNextJourneyTrips.value))
}

// Reset join flags on next journey trips if we are not including them
const resetNextJourneyJoinFlags = () => {
	if (dataStore.currentJourneys.length <= dataStore.currentJourneyIndex + 1) return
	const nextJourney = dataStore.currentJourneys[dataStore.currentJourneyIndex + 1]
	if (nextJourney) {
		nextJourney.trips.forEach((trip) => {
			trip.join = false
		})
	}
}

const resizeSheetForBodyHeight = () => {
	var tripBodyElement = document.querySelector('.page.active .body')
	var bottomSheetElement = document.querySelector('.bottom-sheet')
	if (tripBodyElement && bottomSheetElement) {
		var sheetExcludingBody = bottomSheetElement.clientHeight - tripBodyElement.clientHeight
		setTimeout(() => {
			const hasVerticalScroll = tripBodyElement.scrollHeight > tripBodyElement.clientHeight
			if (hasVerticalScroll) {
				setSheetHeight(tripBodyElement.scrollHeight + sheetExcludingBody, 300, 'TripsPage')
				smoothScroll('.page.active .body', 'bottom', 0)
			}
		}, 300)
	}
}

const toggleEdit = () => {
	// Always reset includeNextJourneyTrips
	//includeNextJourneyTrips.value = false

	if (!appStore.editStep) {
		appStore.editStep = 'change'
		setTimeout(() => {
			appStore.bottomSheet.autoResize(300, true, true, 'TripsPage')
		}, 50)
	} else {
		appStore.editHide = true
		smoothScroll('.page.active .body', 'top', 300)
		setTimeout(() => {
			appStore.editStep = ''
			appStore.editHide = false
		}, 150)
		setTimeout(() => {
			appStore.bottomSheet.autoResize(300, true, true, 'TripsPage')
		}, 200)
	}
}

const changeEditStep = (stepName) => {
	splitIndex.value = -1

	// Disable split trip to journey as default
	splitTripToJourney.value = false

	switch (stepName) {
		case 'addTrip':
			appStore.editStep = stepName
			currentJourney.value.trips.forEach((trip) => (trip.add = false))
			break
		case 'joinTrips':
			if (currentJourney.value?.trips) {
				currentJourney.value.trips.forEach((trip) => (trip.join = false))
			}
			appStore.editStep = stepName
			break

		case 'splitJourney':
			appStore.editStep = stepName
			break

		case 'splitTrip':
			if (currentJourney.value?.trips) {
				currentJourney.value.trips.forEach((trip) => (trip.split = false))
			}
			appStore.editStep = stepName
			break

		case 'splitTripActive':
			appStore.editStep = stepName
			dataStore.currentSplitLocationRangeIndex = 1
			setTimeout(() => {
				dataStore.currentSplitLocationRangeIndex = 2
			}, 50)
			break

		case 'joinPrevJourney':
			appStore.editStep = 'joinPrevJourney'
			appStore.bottomSheet.autoResize(300, true, true, 'TripsPage joinPrevJourney')
			console.warn('joinPrevJourney', dataStore.getTripsForJoin())
			mapStore.plotTripsInner(dataStore.getTripsForJoin())
			break

		case 'joinNextJourney':
			console.warn('Bekreft')
			appStore.editStep = 'joinNextJourney'
			appStore.bottomSheet.autoResize(300, true, true, 'TripsPage joinNextJourney')
			console.warn('joinNextJourney', dataStore.getTripsForJoin())
			mapStore.plotTripsInner(dataStore.getTripsForJoin())
			break

		case 'deleteJourney':
			ElMessageBox.confirm('Er du sikker på at du vil slette hele denne reisen?', 'Slett reise', { confirmButtonText: 'Ja', cancelButtonText: 'Nei' })
				.then(() => {
					dataStore.deleteCurrentJourney()
					appStore.editStep = ''
				})
				.catch(() => {
					appStore.editStep = ''
				})
			break

		case 'deleteTrip':
			appStore.editStep = stepName
			break

		default:
			appStore.futureRelease = true
			break
	}

	setTimeout(() => {
		appStore.bottomSheet.autoResize(300, true, true, 'TripsPage changeEditStep')
	}, 200)
}

const stepWrapperClasses = (trip, index, groupIndex) => {
	// Get all trips from current journey and next journey (if exists and visible)
	const currentJourneyTrips = dataStore.currentJourney.trips
	const nextJourney = dataStore.currentJourneys[dataStore.currentJourneyIndex + 1]
	const nextJourneyTrips = nextJourney && includeNextJourneyTrips.value ? nextJourney.trips : []
	const allTrips = [...currentJourneyTrips, ...nextJourneyTrips]

	// Calculate the global index for cross-journey validation
	const globalIndex = groupIndex === 0 ? index : currentJourneyTrips.length + index

	// Get previous and next trips considering groups
	const prevTrip = globalIndex > 0 ? allTrips[globalIndex - 1] : null
	const nextTrip = globalIndex < allTrips.length - 1 ? allTrips[globalIndex + 1] : null

	return {
		splitted: splitIndex.value == index && groupIndex === 0, // Assuming split only applies to first group
		joined: trip.join,
		lastJoined: trip.join && !nextTrip?.join,
		firstJoined: trip.join && !prevTrip?.join,
	}
}

const tripClasses = (trip) => [getActivityName(trip), { selected: trip.id == dataStore.selectedTripId }, { edited: trip.edited }, { deleted: trip.deleted }]

const tripClickedForJoin = (trip, index, groupIndex) => {
	// Get all trips from current journey and next journey (if exists and visible) for cross-journey validation
	const currentJourneyTrips = dataStore.currentJourney.trips
	const nextJourney = dataStore.currentJourneys[dataStore.currentJourneyIndex + 1]
	const nextJourneyTrips = nextJourney && includeNextJourneyTrips.value ? nextJourney.trips : []
	const allTrips = [...currentJourneyTrips, ...nextJourneyTrips]

	// Calculate the global index for cross-journey validation
	const globalIndex = groupIndex === 0 ? index : currentJourneyTrips.length + index

	// Toggle the clicked trip
	trip.join = !trip.join

	// Get current state of join selections
	const joinStates = allTrips.map((t) => t.join)

	// Find first and last join indices
	let firstJoinIndex = joinStates.indexOf(true)
	let lastJoinIndex = joinStates.lastIndexOf(true)

	// Auto-fill gaps or auto-deselect to maintain consecutive sequence
	if (firstJoinIndex > -1 && lastJoinIndex > -1 && firstJoinIndex !== lastJoinIndex) {
		if (trip.join) {
			// If selecting: fill any gaps between first and last selected
			for (let i = firstJoinIndex; i <= lastJoinIndex; i++) {
				allTrips[i].join = true
			}
		} else {
			// If deselecting: remove all trips that would create a gap
			if (globalIndex > firstJoinIndex && globalIndex < lastJoinIndex) {
				// Deselecting from middle - remove all trips after the gap
				for (let i = globalIndex + 1; i <= lastJoinIndex; i++) {
					allTrips[i].join = false
				}
			}
		}
	}

	hideLastToast()

	// Update joinable status for both journeys
	var currentJoinableTrips = currentJourneyTrips.filter((t) => t.join)
	var nextJoinableTrips = includeNextJourneyTrips.value ? nextJourneyTrips.filter((t) => t.join) : []
	var totalJoinableTrips = currentJoinableTrips.length + nextJoinableTrips.length

	dataStore.currentJourney.joinable = totalJoinableTrips > 1
	if (nextJourney) {
		nextJourney.joinable = includeNextJourneyTrips.value ? totalJoinableTrips > 1 : false
	}

	// Plot trips with updated joinable status
	if (totalJoinableTrips > 0) mapStore.plotTripsInner(dataStore.getTripsForJoin(includeNextJourneyTrips.value))
	else mapStore.showTrips(dataStore.currentJourney.trips)

	console.warn('tripClickedForJoin', trip, index, groupIndex, trip.join, totalJoinableTrips)
}

const tripClicked = (trip, index, groupIndex) => {
	dataStore.selectedTripId = trip.id

	// Keep a reference to the last selected trip
	selectedTrip.value = trip

	console.warn('tripClicked', index, trip)

	if (appStore.joinNextJourney || appStore.joinPrevJourney) {
		var clickedTrip = tripGroups[groupIndex].trips[index]
		console.warn('tripClicked join', index, groupIndex, clickedTrip)
		mapStore.showTrip(clickedTrip)
	}

	if (appStore.editStep === 'addTrip') {
		const currentAddState = trip.add
		dataStore.currentJourney.trips.forEach((t) => (t.add = false))
		if (!currentAddState) trip.add = true
		dataStore.currentJourney.addIndex = currentAddState ? -1 : index + 1
		mapStore.showTrip(trip)
	}

	if (appStore.editStep === 'deleteTrip') {
		const currenDeleteState = trip.delete
		dataStore.currentJourney.trips.forEach((t) => (t.delete = false))
		if (!currenDeleteState) trip.delete = true
		dataStore.currentJourney.deleteIndex = currenDeleteState ? -1 : index
		mapStore.showTrip(trip)
	}

	if (appStore.editStep === 'joinTrips') {
		tripClickedForJoin(trip, index, groupIndex)
	}

	if (appStore.editStep === 'splitTrip') {
		const currentSplitState = trip.split
		dataStore.currentJourney.trips.forEach((t) => (t.split = false))
		if (!currentSplitState) trip.split = true
		dataStore.currentJourney.splittable = trip.split
		mapStore.showTrip(trip)
	}

	if (appStore.editStep == 'change' || appStore.editStep == '') {
		mapStore.showTrip(trip)
	}
}

const updateJourneyOriginDestinationFromTrips = (journey) => {
	journey.origin = journey.trips[0].origin
	journey.destination = journey.trips[journey.trips.length - 1].destination
}

const splitJourney = () => {
	const currentJourney = dataStore.currentJourney
	const currentJourneys = dataStore.currentJourneys

	appStore.editStep = ''
	// toggleEdit

	// Remove trips from currentJourney after split point
	const removedTrips = currentJourney.trips.splice(splitIndex.value + 1)

	// Create new journey from trips after split point
	const newJourney = {
		...JSON.parse(JSON.stringify(currentJourney)),
		trips: removedTrips,
		id: currentJourney.id + '-split',
		approved: false,
		verified: false,
		splitted: true,
	}

	// Update orign and destination for new journey
	updateJourneyOriginDestinationFromTrips(newJourney)

	// Update origin and destination for current journey
	updateJourneyOriginDestinationFromTrips(currentJourney)

	// Update current journey
	currentJourney.edited = true
	currentJourney.approved = false
	currentJourney.verified = false
	currentJourney.splitted = true
	currentJourney.splitIndex = splitIndex.value

	// Insert new journey
	currentJourneys.splice(dataStore.currentJourneyIndex + 1, 0, newJourney)
	splitIndex.value = -1

	// Reset surveys current and new journey
	dataStore.resetSurveysFromJourneyIndex(dataStore.currentJourneyIndex)
}

// const joinTrips = async () => {
// 	const currentJourney = dataStore.currentJourney
// 	if (currentJourney.loading) return

// 	// Get trips from current journey and next journey (if visible)
// 	const currentTrips = currentJourney.trips
// 	const nextJourney = dataStore.currentJourneys[dataStore.currentJourneyIndex + 1]
// 	const nextTrips = nextJourney && includeNextJourneyTrips.value ? nextJourney.trips : []
// 	const allTrips = [...currentTrips, ...nextTrips]

// 	// Find joined trips across both journeys
// 	const joinedTrips = allTrips.map((t) => t.join)
// 	const lastJoinIndex = joinedTrips.lastIndexOf(true)
// 	const firstJoinIndex = joinedTrips.indexOf(true)

// 	if (lastJoinIndex - firstJoinIndex < 1) {
// 		appStore.toast({ message: 'Du må velge minst to etapper for å slå sammen', type: 'error', duration: 3000 })
// 		return
// 	}

// 	const selectedTrips = allTrips.slice(firstJoinIndex, lastJoinIndex + 1)
// 	const joinedLocations = selectedTrips.reduce((acc, trip) => acc.concat(trip.locations), [])
// 	const firstTrip = selectedTrips[0]
// 	const lastTrip = selectedTrips[selectedTrips.length - 1]

// 	const newTrip = {
// 		id: `${firstTrip.id}-${lastTrip.id}`,
// 		index: `${firstTrip.index}-${lastTrip.index}`,
// 		activity_name: firstTrip.activity_name,
// 		departure_timestamp: firstTrip.departure_timestamp,
// 		arrival_timestamp: lastTrip.arrival_timestamp,
// 		distance: window.etl.calculateLocationDistance(firstTrip.origin, lastTrip.destination),
// 		duration: (lastTrip.arrival_timestamp - firstTrip.departure_timestamp) / 1000,
// 		origin: firstTrip.origin,
// 		origin_poi: firstTrip.origin_poi,
// 		destination: lastTrip.destination,
// 		destination_poi: lastTrip.destination_poi,
// 		locations: joinedLocations,
// 		join: false,
// 		edited: true,
// 		joinedTrips: selectedTrips,
// 	}

// 	// Use etl to reclassify trip
// 	currentJourney.loading = true
// 	await window.etl.updateMergedTrip(newTrip, true)
// 	currentJourney.loading = false

// 	// Determine which journeys are affected and where to place the new trip
// 	const currentJourneyTripsCount = currentTrips.length
// 	const crossJourneyJoin = lastJoinIndex >= currentJourneyTripsCount

// 	if (crossJourneyJoin) {
// 		// Cross-journey join: remove selected trips from both journeys
// 		currentJourney.trips = currentTrips.filter((t) => !t.join)
// 		if (nextJourney) {
// 			nextJourney.trips = nextTrips.filter((t) => !t.join)
// 		}

// 		// Place new trip in current journey if first selected trip is from current journey
// 		// Otherwise place in next journey
// 		if (firstJoinIndex < currentJourneyTripsCount) {
// 			// First selected trip is from current journey
// 			const insertIndex = Math.min(firstJoinIndex, currentJourney.trips.length)
// 			currentJourney.trips.splice(insertIndex, 0, newTrip)
// 		} else {
// 			// First selected trip is from next journey
// 			const insertIndex = Math.min(firstJoinIndex - currentJourneyTripsCount, nextJourney.trips.length)
// 			nextJourney.trips.splice(insertIndex, 0, newTrip)
// 		}

// 		// Update both journeys
// 		updateJourneyOriginDestinationFromTrips(currentJourney)
// 		if (nextJourney) {
// 			updateJourneyOriginDestinationFromTrips(nextJourney)
// 		}
// 	} else {
// 		// Single journey join (original logic)
// 		currentJourney.trips = currentTrips.filter((t) => !t.join)
// 		currentJourney.trips.splice(firstJoinIndex, 0, newTrip)
// 		updateJourneyOriginDestinationFromTrips(currentJourney)
// 	}

// 	// Join finished to end edit
// 	appStore.editStep = ''
// 	// Freeze map to avoid automatic replotting while resetting bottom sheet height
// 	mapStore.freezed = true
// 	// Reset bottom sheet height
// 	setSheetHeight(BottomSheetStates.NORMAL, 300, 'joinTrips')
// 	// Reset survey
// 	dataStore.resetSurveysFromJourneyIndex(dataStore.currentJourneyIndex)
// 	// Replot trips
// 	setTimeout(() => {
// 		mapStore.freezed = false
// 		mapStore.plotTrips(Math.min(firstJoinIndex, currentJourneyTripsCount - 1))
// 	}, 500)
// }

const joinTrips = async () => {
	const currentJourney = dataStore.currentJourney
	const nextJourney = dataStore.currentJourneys[dataStore.currentJourneyIndex + 1]

	if (currentJourney.loading || (nextJourney?.loading && includeNextJourneyTrips.value)) return

	// Collect all trips that can be joined
	const currentJourneyTrips = currentJourney.trips
	const nextJourneyTrips = nextJourney && includeNextJourneyTrips.value ? nextJourney.trips : []
	const allTrips = [...currentJourneyTrips, ...nextJourneyTrips]

	// Find selected trips
	const joinedTrips = allTrips.map((t) => t.join)
	const firstJoinIndex = joinedTrips.indexOf(true)
	const lastJoinIndex = joinedTrips.lastIndexOf(true)

	if (lastJoinIndex - firstJoinIndex < 1) {
		appStore.toast({ message: 'Du må velge minst to etapper for å slå sammen', type: 'error', duration: 3000 })
		return
	}

	const selectedTrips = allTrips.slice(firstJoinIndex, lastJoinIndex + 1)
	const joinedLocations = selectedTrips.reduce((acc, trip) => acc.concat(trip.locations), [])
	const firstTrip = selectedTrips[0]
	const lastTrip = selectedTrips[selectedTrips.length - 1]

	var distanceData = window.etl.getDistances(joinedLocations)
	var distance = distanceData.totalDistance

	// Create the new merged trip
	const newTrip = {
		id: `${firstTrip.id}-${lastTrip.id}`,
		index: `${firstTrip.index}-${lastTrip.index}`,
		activity_name: firstTrip.activity_name,
		departure_timestamp: firstTrip.departure_timestamp,
		arrival_timestamp: lastTrip.arrival_timestamp,
		distance: distance,
		duration: (lastTrip.arrival_timestamp - firstTrip.departure_timestamp) / 1000,
		origin: firstTrip.origin,
		origin_poi: firstTrip.origin_poi,
		destination: lastTrip.destination,
		destination_poi: lastTrip.destination_poi,
		locations: joinedLocations,
		join: false,
		edited: true,
		joinedTrips: selectedTrips,
	}

	// Use etl to reclassify trip
	currentJourney.loading = true
	if (nextJourney && includeNextJourneyTrips.value) nextJourney.loading = true

	await window.etl.updateMergedTrip(newTrip, true)

	currentJourney.loading = false
	if (nextJourney && includeNextJourneyTrips.value) nextJourney.loading = false

	// Determine which journey the new trip belongs to
	const firstJoinInCurrentJourney = firstJoinIndex < currentJourneyTrips.length
	const lastJoinInCurrentJourney = lastJoinIndex < currentJourneyTrips.length

	// Handle the three scenarios
	if (firstJoinInCurrentJourney && lastJoinInCurrentJourney) {
		// Scenario 1: All joined trips are in current journey
		currentJourney.trips = currentJourneyTrips.filter((t) => !t.join)
		currentJourney.trips.splice(firstJoinIndex, 0, newTrip)
		updateJourneyOriginDestinationFromTrips(currentJourney)
	} else if (firstJoinInCurrentJourney && !lastJoinInCurrentJourney) {
		// Scenario 2: Joined trips span both journeys

		// Remove joined trips from current journey
		const currentJourneyJoinedTrips = currentJourneyTrips.filter((t) => t.join)
		currentJourney.trips = currentJourneyTrips.filter((t) => !t.join)

		// Remove joined trips from next journey
		const nextJourneyJoinedTrips = nextJourneyTrips.filter((t) => t.join)
		nextJourney.trips = nextJourneyTrips.filter((t) => !t.join)

		// Add new trip to current journey at the first join position
		currentJourney.trips.splice(firstJoinIndex, 0, newTrip)

		// Update both journeys
		updateJourneyOriginDestinationFromTrips(currentJourney)

		// Handle empty next journey
		if (nextJourney.trips.length === 0) {
			// Remove the empty journey
			dataStore.currentJourneys.splice(dataStore.currentJourneyIndex + 1, 1)
		} else {
			updateJourneyOriginDestinationFromTrips(nextJourney)
		}

		// Mark current journey as edited
		currentJourney.edited = true
		currentJourney.approved = false
		currentJourney.verified = false
	} else if (!firstJoinInCurrentJourney && !lastJoinInCurrentJourney) {
		// Scenario 3: All joined trips are in next journey
		const adjustedFirstJoinIndex = firstJoinIndex - currentJourneyTrips.length
		nextJourney.trips = nextJourneyTrips.filter((t) => !t.join)
		nextJourney.trips.splice(adjustedFirstJoinIndex, 0, newTrip)
		updateJourneyOriginDestinationFromTrips(nextJourney)

		// Mark next journey as edited
		nextJourney.edited = true
		nextJourney.approved = false
		nextJourney.verified = false
	}

	// Reset joinable flags
	currentJourney.joinable = false
	if (nextJourney) nextJourney.joinable = false

	// Reset include next journey trips
	includeNextJourneyTrips.value = false

	// Join finished to end edit
	appStore.editStep = ''

	// Freeze map to avoid automatic replotting while resetting bottom sheet height
	mapStore.freezed = true

	// Reset bottom sheet height
	setSheetHeight(BottomSheetStates.NORMAL, 300, 'joinTrips')

	// Reset survey from current journey index
	dataStore.resetSurveysFromJourneyIndex(dataStore.currentJourneyIndex)

	// Replot trips
	setTimeout(() => {
		mapStore.freezed = false
		// Always plot current journey after joining
		mapStore.plotTrips(firstJoinInCurrentJourney ? firstJoinIndex : 0)
	}, 500)
}

const joinTrips_ORG = async () => {
	const currentJourney = dataStore.currentJourney
	if (currentJourney.loading) return

	const trips = currentJourney.trips
	const joinedTrips = trips.map((t) => t.join)
	const lastJoinIndex = joinedTrips.lastIndexOf(true)
	const firstJoinIndex = joinedTrips.indexOf(true)

	if (lastJoinIndex - firstJoinIndex < 1) {
		appStore.toast({ message: 'Du må velge minst to etapper for å slå sammen', type: 'error', duration: 3000 })
		return
	}

	const selectedTrips = trips.slice(firstJoinIndex, lastJoinIndex + 1)
	const joinedLocations = selectedTrips.reduce((acc, trip) => acc.concat(trip.locations), [])

	const firstTrip = selectedTrips[0]
	const lastTrip = selectedTrips[selectedTrips.length - 1]

	const newTrip = {
		id: `${firstTrip.id}-${lastTrip.id}`,
		index: `${firstTrip.index}-${lastTrip.index}`,
		activity_name: firstTrip.activity_name,
		departure_timestamp: firstTrip.departure_timestamp,
		arrival_timestamp: lastTrip.arrival_timestamp,
		distance: window.etl.calculateLocationDistance(firstTrip.origin, lastTrip.destination),
		duration: (lastTrip.arrival_timestamp - firstTrip.departure_timestamp) / 1000,
		origin: firstTrip.origin,
		origin_poi: firstTrip.origin_poi,
		destination: lastTrip.destination,
		destination_poi: lastTrip.destination_poi,
		locations: joinedLocations,
		//mode: selectedTrips[0].mode,
		join: false,
		edited: true,
		joinedTrips: selectedTrips,
	}

	// Use etl to reclassify trip
	currentJourney.loading = true
	await window.etl.updateMergedTrip(newTrip, true)
	currentJourney.loading = false

	// Remove joined trips from currentJourney
	currentJourney.trips = trips.filter((t) => !t.join)

	// Insert new trip as the same index as the first join trip
	currentJourney.trips.splice(firstJoinIndex, 0, newTrip)

	// Update origin and destination for current journey
	updateJourneyOriginDestinationFromTrips(currentJourney)

	// Join finished to end edit
	appStore.editStep = ''

	// Freeze map to avoid automatic replotting while resetting bottom sheet height
	mapStore.freezed = true

	// Reset bottom sheet height
	setSheetHeight(BottomSheetStates.NORMAL, 300, 'joinTrips')

	// Reset survey
	dataStore.resetSurveysFromJourneyIndex(dataStore.currentJourneyIndex)

	// Replot trips
	setTimeout(() => {
		mapStore.freezed = false
		mapStore.plotTrips(firstJoinIndex)
	}, 500)
}

const finishDeleteTrip = () => {
	const currentJourney = dataStore.currentJourney
	currentJourney.trips.forEach((trip) => (trip.delete = false))
	currentJourney.deleteIndex = -1
	appStore.editStep = 'change'
	setTimeout(() => {
		appStore.bottomSheet.autoResize(300, true, true, 'TripsPage finishDeleteTrip')
	}, 200)
}

const cancelAddTrip = () => {
	const currentJourney = dataStore.currentJourney
	currentJourney.trips.forEach((trip) => (trip.add = false))
	currentJourney.addIndex = -1
	appStore.editStep = 'change'
	setTimeout(() => {
		appStore.bottomSheet.autoResize(300, true, true, 'TripsPage cancelAddTrip')
	}, 200)
}

const cancelJoinJourneys = () => {
	appStore.editStep = ''
	setTimeout(() => {
		//mapStore.plotTripsInner(currentJourney.trips)
		mapStore.plotTrips()
		appStore.bottomSheet.autoResize(300, true, true, 'TripsPage cancelJoinTrips')
	}, 200)
}

const cancelJoinTrips = () => {
	// Reset join flags for current journey
	const currentJourney = dataStore.currentJourney
	currentJourney.trips.forEach((trip) => (trip.join = false))
	currentJourney.joinable = false

	// Reset join flags for next journey if it exists and was included
	const nextJourney = dataStore.currentJourneys[dataStore.currentJourneyIndex + 1]
	if (nextJourney && includeNextJourneyTrips.value) {
		nextJourney.trips.forEach((trip) => (trip.join = false))
		nextJourney.joinable = false
	}

	// Reset UI state
	appStore.editStep = 'change'

	// Turn off include next journey trips
	includeNextJourneyTrips.value = false

	// Restore the map to show current journey trips
	mapStore.showTrips(currentJourney.trips)

	setTimeout(() => {
		appStore.bottomSheet.autoResize(300, true, true, 'TripsPage cancelJoinTrips')
	}, 200)
}

const cancelSplitTrip = () => {
	const currentJourney = dataStore.currentJourney
	currentJourney.trips.forEach((trip) => (trip.split = false))
	currentJourney.splittable = false
	splitIndex.value = -1
	toggleEdit()
}

const cancelSplitJourney = () => {
	const currentJourney = dataStore.currentJourney
	currentJourney.trips.forEach((trip) => (trip.join = false))
	currentJourney.joinable = false
	splitIndex.value = -1
	appStore.editStep = 'change'
}

const hideLastToast = () => {
	// TODO: Imeplent TOAST
}

const splitLocationSliderTouch = (pressed) => {
	appStore.showSplitLocation = pressed
}

const joinJourneysClick = () => {
	if (appStore.editStep === 'joinNextJourney') {
		joinNextJourney()
	} else if (appStore.editStep === 'joinPrevJourney') {
		joinPrevJourney()
	}
	appStore.editStep = ''
	appStore.bottomSheet.autoResize(300, true, true, 'TripsPage joinJourneysClick')
}

const joinNextJourney = () => {
	const currentJourneys = dataStore.currentJourneys
	const currentJourney = dataStore.currentJourney
	const nextJourney = currentJourneys[dataStore.currentJourneyIndex + 1]
	currentJourney.trips = [...currentJourney.trips, ...nextJourney.trips]
	currentJourney.edited = true
	currentJourney.approved = false
	currentJourney.verified = false
	currentJourney.joined = true
	currentJourney.joinIndex = dataStore.currentJourneyIndex + 1
	currentJourneys.splice(dataStore.currentJourneyIndex + 1, 1)

	// Update origin and destination for current journey
	updateJourneyOriginDestinationFromTrips(currentJourney)

	// Reset surveys from current index
	dataStore.resetSurveysFromJourneyIndex(dataStore.currentJourneyIndex)
}

const joinPrevJourney = () => {
	const currentJourneys = dataStore.currentJourneys
	const currentJourney = dataStore.currentJourney
	const prevJourney = currentJourneys[dataStore.currentJourneyIndex - 1]
	currentJourney.trips = [...prevJourney.trips, ...currentJourney.trips]
	currentJourney.edited = true
	currentJourney.approved = false
	currentJourney.verified = false
	currentJourney.joined = true
	currentJourney.joinIndex = dataStore.currentJourneyIndex - 1
	currentJourneys.splice(dataStore.currentJourneyIndex - 1, 1)

	// Update origin and destination for current journey
	updateJourneyOriginDestinationFromTrips(currentJourney)

	// Got back to previous index
	dataStore.currentJourneyIndex--

	// Reset surveys from current index
	dataStore.resetSurveysFromJourneyIndex(dataStore.currentJourneyIndex)
}

const clearJourneyApproval = () => {
	const currentJourney = dataStore.currentJourney
	if (!currentJourney) return

	currentJourney.approved = false
	currentJourney.verified = false

	// Restore journey to original state if needed
	if (dataStore.originalJourneyDays.length > 0) {
		const originalJourneyDay = dataStore.originalJourneyDays.find((journeyDay) => journeyDay.date === currentJourney.date)

		if (originalJourneyDay?.journeys) {
			// Find journey where first trip matches current journey's first trip
			const originalJourney = originalJourneyDay.journeys.find((journey) => journey.trips[0].departure_timestamp === currentJourney.trips[0].departure_timestamp)
			// Overwrite currentJourney trips with original trips
			if (originalJourney) {
				currentJourney.trips = originalJourney.trips
			}
		}
	}

	// Readd survey if needed
	dataStore.addSurveyToCurrentJourney()

	// Save changes
	dataStore.saveCurrentTravelDay()
}

const setSplitIndex = (index) => {
	splitIndex.value = index
	dataStore.splitIndex = index
}

const setFutureRelease = (value) => {
	appStore.futureRelease = value
}

const getTransportDescription = (trip) => {
	const type = getActivityName(trip)
	const transportMode = dataStore.transportModes.find((m) => m.type === type)

	if (transportMode) {
		if (trip.pt) {
			const publicCode = trip.pt.public_code
			const destinationName = trip.pt.transport_mode === 'metro' ? `${publicCode} ${trip.pt.destination_name ?? ''}` : publicCode
			return `${transportMode.name} ${destinationName}`
		}
		return transportMode.desc
	}
	return 'Ukjent'
}

const getActivityName = (trip) => {
	if (trip && trip.pt) {
		if (trip.original_activity_name && trip.original_activity_name != trip.activity_name) return trip.activity_name
		return trip.pt.type ?? trip.pt.transport_mode
	}
	return trip?.activity_name ?? ''
}

const homeClicked = () => {
	if (appStore.editStep === 'addTrip') {
		dataStore.currentJourney.trips.forEach((t) => (t.add = false))
		dataStore.currentJourney.addIndex = dataStore.currentJourney.addIndex == 0 ? -1 : 0
		mapStore.showTrip(dataStore.currentFirstTrip)
	}
	navigateStart()
}

const navigateStart = () => {
	let trip = {
		id: dataStore.currentFirstTrip.id,
		activity_name: dataStore.currentFirstTrip.activity_name,
		origin: dataStore.currentFirstTrip.origin,
		destination: dataStore.currentFirstTrip.origin,
		locations: [dataStore.currentFirstTrip.origin],
	}
	mapStore.showTrip(trip)
	mapStore.flyToLocation(dataStore.currentFirstTrip.origin, 16)
}

const navigateEnd = () => {
	let trip = {
		id: dataStore.currentLastTrip.id,
		activity_name: dataStore.currentLastTrip.activity_name,
		origin: dataStore.currentLastTrip.destination,
		destination: dataStore.currentLastTrip.destination,
		locations: [dataStore.currentLastTrip.destination],
	}
	mapStore.showTrip(trip)
	mapStore.flyToLocation(dataStore.currentLastTrip.destination, 15)
}

const verifyJourney = () => {
	if (!dataStore.currentJourney) return

	// Check if journey has survey trips are not completed
	if (dataStore.currentJourney.hasTripSurveys && !dataStore.currentJourney.surveyTripsCompleted) {
		appStore.editStep = 'surveyTrip'
		dataStore.evaluateCurrentJourneySurveyResponses()
		//appStore.bottomSheet.autoResize(300, true, true, 'TripsPage')
		appStore.bottomSheet.setHeightFullscreen(500, 'TripsPage', true)
	} else {
		dataStore.currentJourney.verified = true
		dataStore.currentJourney.verified_timestamp = new Date().getTime()

		// Save current travel day
		dataStore.saveCurrentTravelDay()

		// Change to approve page
		appStore.setPage('approve')

		// Reset editstep after page animation
		setTimeout(() => {
			appStore.editStep = ''
		}, 500)
	}
}

const editTrip = (trip, index) => {
	dataStore.selectedTripId = trip.id
	dataStore.editTrip(trip, index)
	appStore.setPage('edit')
}

const deleteTrip = () => {
	const tripToDelete = dataStore.currentJourney.trips[dataStore.currentJourney.deleteIndex]
	//tripToDelete.deleted = tripToDelete.deleted ? false : true
	dataStore.deleteTrip(tripToDelete)
	finishDeleteTrip()
	appStore.editStep = ''
	mapStore.plotTrips()
	// setTimeout(() => {
	// 	mapStore.showTrip(tripToDelete)
	// }, 500)
}

const addTrip = () => {
	dataStore.addTrip()
	mapStore.currentEditTrip = dataStore.currentEditTrip
	mapStore.currentEditTripIsNew = true
	mapStore.plotCurrentEdit()
	appStore.setPage('edit')
}

const addJourney = (orderjourney) => {
	dataStore.addJourney(orderjourney)
	mapStore.currentEditTrip = dataStore.currentEditTrip
	mapStore.currentEditTripIsNew = true
	mapStore.plotCurrentEdit()
	appStore.setPage('edit')
}

const performCurrentSplit = async () => {
	if (!dataStore.currentSplitTrip || !dataStore.currentJourney?.splittable) return
	if (dataStore.currentJourney.loading) return

	const splitTrip = dataStore.currentSplitTrip

	if (dataStore.currentSplitLocationIndex > 0 && dataStore.currentSplitLocationIndex < splitTrip.locations.length - 1) {
		// Create new trip from split point
		const newSplittedTrip = {
			activity_name: splitTrip.activity_name,
			locations: splitTrip.locations.slice(dataStore.currentSplitLocationIndex),
			origin: splitTrip.locations[dataStore.currentSplitLocationIndex],
			destination: splitTrip.locations[splitTrip.locations.length - 1],
			destination_poi: splitTrip.destination_poi,
			origin_pois: [],
			split: false,
			splitted: true,
			splitLocationIndex: dataStore.currentSplitLocationIndex,
			edited: true,
		}

		newSplittedTrip.departure_timestamp = newSplittedTrip.origin.timestamp
		newSplittedTrip.arrival_timestamp = newSplittedTrip.destination.timestamp
		newSplittedTrip.duration = (newSplittedTrip.arrival_timestamp - newSplittedTrip.departure_timestamp) / 1000
		newSplittedTrip.distance = window.etl.calculateLocationDistance(newSplittedTrip.origin, newSplittedTrip.destination)

		if (splitTrip.pt) {
			newSplittedTrip.activity_name = 'automotive'
			newSplittedTrip.mode = 'automotive'
		}

		// Insert new trip
		dataStore.currentJourney.trips.splice(dataStore.currentSplitTripIndex + 1, 0, newSplittedTrip)

		// Update trip IDs
		dataStore.currentJourney.trips.forEach((trip, index) => {
			trip.id = index
			trip.index = index
		})

		// Update original trip
		splitTrip.locations = splitTrip.locations.slice(0, dataStore.currentSplitLocationIndex + 1)
		splitTrip.destination = splitTrip.locations[splitTrip.locations.length - 1]
		splitTrip.arrival_timestamp = splitTrip.destination.timestamp
		splitTrip.duration = (splitTrip.arrival_timestamp - splitTrip.departure_timestamp) / 1000
		splitTrip.distance = window.etl.calculateLocationDistance(splitTrip.origin, splitTrip.destination)

		if (splitTrip.pt) {
			splitTrip.activity_name = 'automotive'
			splitTrip.mode = 'automotive'
			delete splitTrip.pt
		}

		dataStore.currentJourney.loading = true
		await window.etl.updateMergedTrip(newSplittedTrip, true)
		await window.etl.updateMergedTrip(splitTrip, true)
		dataStore.currentJourney.loading = false

		// Update split properties after everything else
		if (!splitTripToJourney.value) splitTrip.split = false

		splitTrip.splitted = true
		splitTrip.splitLocationIndex = dataStore.currentSplitLocationIndex
		splitTrip.edited = true

		const mapStore = useMapStore()
		mapStore.removeCurrentSplitLocationMarker()
		mapStore.freezed = true

		// End edit mode
		toggleEdit()

		// Check if we should split further into journeys
		if (splitTripToJourney.value) {
			splitIndex.value = dataStore.currentSplitTripIndex
			splitTrip.split = false
			splitJourney()

			setTimeout(() => {
				mapStore.freezed = false
				mapStore.plotTrips()
			}, 500)
		} else {
			// Add survey to current journey again
			dataStore.addSurveyToCurrentJourney()

			setTimeout(() => {
				mapStore.freezed = false
				mapStore.plotTrips(dataStore.currentSplitTripIndex)
			}, 500)
		}
	}
}

const tripGroups = computed(() => {
	if (appStore.editStep == 'joinPrevJourney') {
		return [dataStore.currentJourneys[dataStore.currentJourneyIndex - 1].trips, dataStore.currentJourney.trips]
	} else if (appStore.editStep == 'joinNextJourney' || includeNextJourneyTrips.value) {
		return [dataStore.currentJourney.trips, dataStore.currentJourneys[dataStore.currentJourneyIndex + 1].trips]
	} else {
		return [dataStore.currentJourney.trips]
	}
})

const previousTripSurveyCompleted = (index) => {
	console.warn('TripsPage: previousTripSurveyCompleted', index)

	if (index == 0 || dataStore.currentJourney.trips.length <= index) {
		// Lets check if we can prepopulate the current trip survey based on the previous journey
		if (dataStore.currentJourneyIndex > 0) {
			autoCompleteSurveyForJourneyTrip(dataStore.currentJourney.trips[0], dataStore.currentJourneyDay.autofill)
			dataStore.evaluateCurrentJourneySurveyResponses()
		}
		return true
	}
	var previousCompletedIndex = 0

	// Check if current journey has a trip with a survey which is not completed, before the current trip
	for (let i = 0; i < index; i++) {
		// Return false if trips before the current trip has a survey which is not completed
		if (dataStore.currentJourney.trips[i].survey && !dataStore.currentJourney.trips[i].survey.completed) return false
		else previousCompletedIndex = i
	}

	// The previous trip survey is completed, lets check if we can prepopulate the current trip survey
	autoCompleteSurveyForJourneyTrip(dataStore.currentJourney.trips[index], dataStore.currentJourneyDay.autofill)

	// Evalueate after auto completion
	dataStore.evaluateCurrentJourneySurveyResponses()

	return true
}

const surveyChanged = (eventData) => {
	dataStore.evaluateCurrentJourneySurveyResponses()
	// setTimeout(() => {
	// 	appStore.bottomSheet.autoResize(300, true, true, 'TripsPage surveyChanged')
	// }, 0)
	// Extract answers for later auto completion

	// Get the activity type from the trip data
	const activityType = eventData.trip.activity_name

	//const cleanId = eventData.survey?.id?.match(/^(BIL_PASSASJER_DRIVSTOFF|TRANSPORTMIDDEL|BIL_SJOFOR|BIL_EIE|LASTESYKKEL)_R/)?.[1]

	const autofilledCodes = {
		BIL_SJOFOR: {},
		BIL_EIE: {},
		BIL_PASSASJER_DRIVSTOFF: {},
		LASTESYKKEL: {},
		TRANSPORTMIDDEL: {
			3: 'Bil som sjåfør',
			4: 'Bil som passasjer',
			8: 'Vanlig sykkel',
			9: 'Elsykkel',
			10: 'Bysykkel',
			11: 'Elsparkesykkel (privat)',
			12: 'Elsparkesykkel (leie)',
			28: 'Mopedbil',
		},
	}

	// Extract the base ID by removing the _R suffix
	const baseId = eventData.survey?.id?.replace(/_R$/, '')

	// Find matching key that starts with the baseId
	const autofillId = Object.keys(autofilledCodes).find((key) => baseId?.startsWith(key))

	if (autofillId) {
		let storeAutofill = false
		if (autofilledCodes[autofillId]) {
			if (Object.keys(autofilledCodes[autofillId]).length > 0) {
				if (autofilledCodes[autofillId].hasOwnProperty(eventData.selectedOption.code)) storeAutofill = true
				else {
					delete dataStore.currentJourneyDay.autofill[activityType]
				}
			} else {
				storeAutofill = true // If no specific codes, store all
			}
		}
		if (storeAutofill) {
			// If dataStore.currentJourneyDay.autofill property does not exist, create it
			if (!dataStore.currentJourneyDay.autofill) dataStore.currentJourneyDay.autofill = {}
			// Add activityType to autofill if it does not exist
			if (!dataStore.currentJourneyDay.autofill.hasOwnProperty(activityType)) dataStore.currentJourneyDay.autofill[activityType] = {}
			// Store the selected option in the autofill object
			dataStore.currentJourneyDay.autofill[activityType][autofillId] = eventData.selectedOption
		}
	}

	console.warn('ApprovePage: surveyChanged', baseId, autofillId, eventData)

	// if (cleanId) {
	// 	let storeAutofill = false
	// 	if (cleanId == 'LASTESYKKEL' || cleanId == 'BIL_PASSASJER_DRIVSTOFF' || cleanId == 'BIL_EIE' || cleanId == 'BIL_SJOFOR' || (cleanId == 'TRANSPORTMIDDEL' && (eventData.selectedOption.code == '3' || eventData.selectedOption.code == '4' || eventData.selectedOption.code == '9' || eventData.selectedOption.code == '28')))) storeAutofill = true

	// 	if (storeAutofill) {
	// 		// Add activityType to autofill if it does not exist
	// 		if (!dataStore.currentJourneyDay.autofill.hasOwnProperty(activityType)) dataStore.currentJourneyDay.autofill[activityType] = {}
	// 		// Store the selected option in the autofill object
	// 		dataStore.currentJourneyDay.autofill[activityType][cleanId] = eventData.selectedOption
	// 	}
	// }
	dataStore.evaluateCurrentJourneySurveyResponses()
}

const surveyOptionsOpened = (isOpen) => {
	console.warn('ApprovePage: surveyOptionsOpened', isOpen)
	setTimeout(() => {
		document.querySelector('.journey .question:has(.selected.open +.items)')?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
	}, 0)
}

const active = () => {
	console.warn('TripsPage: active')

	// Set current journey index to the first unapproved journey but not if we are coming from edit page
	if (appStore.previousPage != 'edit' && !appStore.navigatedBack) {
		// Iterate dataStore.currentJourneys and find the inded of the first which does not have approved property or approved is false
		var firstUnapprovedIndex = dataStore.currentJourneys.findIndex((journey) => !journey.approved)

		// If none is found show the first journey
		if (firstUnapprovedIndex < 0) firstUnapprovedIndex = 0

		// Change currentJourneyIndex (which changes the currentJourney)
		dataStore.currentJourneyIndex = firstUnapprovedIndex
	}

	// Load survey for current journey
	if (dataStore.currentJourney && !dataStore.currentJourney.surveyTripsCompleted) {
		// Add survey to current journey, this will also try to evaluate it for auto completion
		dataStore.addSurveyToCurrentJourney()
	}

	mapStore.plotTrips()

	// Resize bottom sheet after a small delay
	setTimeout(() => {
		appStore.bottomSheet.autoResize(500, true, false, 'tripPage Init')
	}, 200)
}

watch(
	() => dataStore.currentSplitLocationRangeIndex,
	(newRangeIndexValue) => {
		let splitTripLocationResult = mapStore.updateCurrentSplitLocationMarker(dataStore.currentSplitTrip, newRangeIndexValue)
		dataStore.currentSplitLocation = splitTripLocationResult.location
		dataStore.currentSplitLocationIndex = splitTripLocationResult.locationIndex
	},
)
watch(
	() => dataStore.currentJourneyIndex,
	() => {
		if (appStore.editStep) {
			resizeSheetForBodyHeight()
		}
	},
)
</script>

<template>
	<BasePage name="trips" :class="pageClasses" @active="active()" :hasTripsNavigation="true" :hasDatePicker="true" :innerFlex="appStore.editStep != '' && appStore.editStep != 'surveyTrip'">
		<template #header>
			<DefaultHeader :hasTripsNavigation="true" :hasDatePicker="true" />
		</template>

		<div class="journey" v-if="dataStore.currentJourney && dataStore.currentJourney.trips">
			<!-- Start location -->
			<div class="step-wrapper">
				<div class="step visit first" @click="homeClicked()" :class="{ home: dataStore.currentFirstTrip?.from_home, work: dataStore.currentFirstTrip?.from_work }">
					<div class="icon"><i class="fas"></i></div>
					<div class="info">
						<strong>
							Avreise
							{{ dataStore.currentFirstTrip?.from_work ? 'arbeidsplass' : '' }}
							{{ dataStore.currentFirstTrip?.from_home ? 'hjemme' : '' }}
						</strong>
						<div class="metric">Avreise {{ dataStore.formatTime(dataStore.currentFirstTrip?.departure_timestamp) }}</div>
					</div>
					<div class="checkbox" v-if="appStore.editStep == 'addTrip'">
						<div class="button small" :class="[dataStore.currentJourney.addIndex == 0 ? 'blue' : 'light']">
							<i class="fas fa-check" :class="{ transparent: dataStore.currentJourney.addIndex != 0 }"></i>
						</div>
					</div>
				</div>
				<div :class="[getActivityName(dataStore.currentJourney.trips[0])]" class="step path"></div>
			</div>

			<!-- Trip steps -->
			<div class="step-group" v-for="(tripGroup, groupIndex) in tripGroups" :key="`group-${groupIndex}-${tripGroup.id}`">
				<div class="step-wrapper link" v-if="appStore.editStep == 'joinTrips' && includeNextJourneyTrips && groupIndex == 1 && tripGroups.length > 1">
					<div class="smallbox white border flex justify-between" @click="toggleIncludeNextJourneyTrips">
						<div>Ta med etappene fra neste reise</div>
						<ToggleSwitch small v-model="includeNextJourneyTrips" />
					</div>
					<div :class="[getActivityName(tripGroup[0])]" class="step path"></div>
				</div>

				<div class="step-wrapper link" v-if="tripGroups.length > 1 && groupIndex == 1 && !includeNextJourneyTrips">
					<div class="step link activity" v-if="tripGroups.length > 1 && groupIndex == 1">
						<div v-if="!includeNextJourneyTrips" class="icon"><i class="fas"></i></div>
						<div class="line"></div>
					</div>
					<div :class="[getActivityName(tripGroup[0])]" class="step path"></div>
				</div>

				<div class="step-wrapper" v-for="(trip, index) in tripGroup" :key="`trip-${index}-${trip.id}`" :class="stepWrapperClasses(trip, index, groupIndex)" @click="tripClicked(trip, index, groupIndex)">
					<div class="step activity" :class="tripClasses(trip)">
						<div class="icon"><i class="fas"></i></div>
						<div class="info">
							<strong class="description">
								{{ trip.original_activity_name && trip.original_activity_name != trip.activity_name ? dataStore.getTransportMode(trip.activity_name)?.name : getTransportDescription(trip) }}
								<span v-if="trip.activity_name != trip?.original_activity_name" class="strike"> {{ dataStore.getTransportMode(trip.original_activity_name)?.name }}</span>
							</strong>
							<div class="time">
								<span class="strike mr-1" v-if="trip.original_departure_timestamp && dataStore.formatTime(trip?.original_departure_timestamp) != dataStore.formatTime(trip.departure_timestamp)"> {{ dataStore.formatTime(trip?.original_departure_timestamp) }}</span>
								<span class="departure">{{ dataStore.formatTime(trip.departure_timestamp) }}</span>
								<span class="dash"> - </span>
								<span class="strike mr-1" v-if="trip.original_arrival_timestamp && dataStore.formatTime(trip?.original_arrival_timestamp) != dataStore.formatTime(trip.arrival_timestamp)">
									{{ dataStore.formatTime(trip?.original_arrival_timestamp) }}
								</span>
								<span class="arrival">{{ dataStore.formatTime(trip.arrival_timestamp) }}</span>
							</div>
						</div>

						<div class="metrics" v-if="!trip.pt">
							<div class="metric">{{ dataStore.formatDistance(trip.distance) }}</div>
							<div class="metric">
								{{ !trip.deleted ? dataStore.formatDurationFromSeconds(trip.duration) : 'Slettet' }}
							</div>
						</div>
						<div class="metrics" v-if="trip.pt">
							<div class="metric">{{ trip.pt.origin_stop_name }}</div>
							<div class="metric">
								{{ !trip.deleted ? trip.pt.destination_stop_name : 'Slettet' }}
							</div>
						</div>

						<div class="action">
							<div class="button small" @click.stop="editTrip(trip, index)">
								<i class="fas fa-edit"></i>
							</div>
						</div>
						<div class="checkbox">
							<div class="button small" :class="[trip.join || trip.split || trip.add || trip.delete ? 'checked' : 'light']">
								<i class="fas fa-check" :class="{ transparent: !trip.join && !trip.split && !trip.add && !trip.delete }"></i>
							</div>
						</div>
					</div>

					<div class="step split" v-if="index < dataStore.currentJourney.trips.length - 1 && appStore.editStep == 'splitJourney'" :class="{ selected: splitIndex == index }" @click.stop="setSplitIndex(index)">
						<div class="line"></div>
						<div class="button blue small"><i class="fas fa-cut"></i></div>
					</div>

					<template v-if="trip.survey && appStore.editStep == 'surveyTrip' && previousTripSurveyCompleted(index)">
						<div class="step survey" :class="[getActivityName(trip)]">
							<div class="step path"></div>
							<div class="content">
								<TripSurveyQuestion :survey="trip.survey" :trip="trip" @change="surveyChanged" @open="surveyOptionsOpened" />
							</div>
						</div>
					</template>

					<div :class="[getActivityName(trip), { expanded: appStore.editStep == 'splitJourney' }]" class="step path"></div>
				</div>
			</div>

			<!-- End location -->
			<div class="step visit last" @click="navigateEnd" :class="{ home: dataStore.currentLastTrip?.to_home, work: dataStore.currentLastTrip?.to_work }">
				<div class="icon"><i class="fas"></i></div>
				<div class="info">
					<strong>
						Ankomst {{ dataStore.currentLastTrip?.to_work ? 'arbeidsplass' : '' }}
						{{ dataStore.currentLastTrip?.to_home ? 'hjemme' : '' }}
					</strong>
					<div class="metric">Ankomst {{ dataStore.formatTime(dataStore.currentLastTrip?.arrival_timestamp) }}</div>
				</div>
			</div>

			<div class="mt-8 smallbox white border flex justify-between" @click="toggleIncludeNextJourneyTrips" v-if="appStore.editStep == 'joinTrips' && !includeNextJourneyTrips && dataStore.currentJourneyIndex < dataStore.currentJourneys.length - 1">
				<div>Ta med etappene fra neste reise</div>
				<ToggleSwitch small v-model="includeNextJourneyTrips" />
			</div>
		</div>

		<div class="mt-8 hidden-below visible" v-if="dataStore.currentJourney.created" :class="{ hide: appStore.editHide || appStore.editStep != '' }">
			<div class="button white border" @click="addJourney(1)">
				<i class="fas fa-plus"></i>
				<span>Legg til ny reise etter denne</span>
			</div>
		</div>

		<!-- Edit controls -->
		<div class="mt-8 hidden-below" :class="{ visible: appStore.editStep == 'change', hide: appStore.editHide }">
			<div class="grid gap-6 mb-4 grid-cols-2">
				<ButtonMenu color="orange" full>
					<template #button-content><i class="fas fa-down-left-and-up-right-to-center"></i>&nbsp;Del opp </template>
					<li @click="changeEditStep('splitTrip')">
						<div class="flex">
							<div><i class="fas fa-street-view"></i>&nbsp; En etappe</div>
							<div class="description">Deler valgt etappe til to etapper eller reiser på angitt sted</div>
						</div>
					</li>
					<li v-if="dataStore.currentJourney.trips.length > 1" @click="changeEditStep('splitJourney')">
						<div class="flex">
							<div><i class="fa-solid fa-bars-staggered"></i>&nbsp; Denne reisen</div>
							<div class="description">Deler reisen til to reiser med ulikt formål etter valgt etappe.</div>
						</div>
					</li>
				</ButtonMenu>
				<ButtonMenu v-if="dataStore.currentJourneys.length > 1 || dataStore.currentJourney.trips.length > 1" color="orange" full right>
					<template #button-content><i class="fas fa-down-left-and-up-right-to-center"></i>&nbsp;Slå sammen</template>
					<li v-if="dataStore.currentJourney.trips.length > 0" @click="changeEditStep('joinTrips')">
						<div class="flex">
							<div><i class="fas fa-street-view"></i>&nbsp; Etapper</div>
							<div class="description">Slår sammen valgte etapper til èn større etappe</div>
						</div>
					</li>
					<li v-if="dataStore.currentJourneyIndex > 0" @click="changeEditStep('joinPrevJourney')">
						<div class="flex">
							<div><i class="fas fa-route"></i>&nbsp;<i class="fas fa-caret-left"></i>&nbsp;Forrige reise</div>
							<div class="description">Slår sammen denne reisen med forrige reise</div>
						</div>
					</li>
					<li v-if="dataStore.currentJourneyIndex < currentJourneys.length - 1" @click="changeEditStep('joinNextJourney')">
						<div class="flex">
							<div>Neste reise&nbsp; <i class="fa-solid fa-caret-right"></i>&nbsp;<i class="fas fa-route"></i></div>
							<div class="description">Slår sammen denne reisen med neste reise</div>
						</div>
					</li>
				</ButtonMenu>
			</div>
			<div class="grid gap-6 grid-cols-2">
				<ButtonMenu color="blue" full>
					<template #button-content><i i class="fas fa-plus"></i>&nbsp;Legg til ny</template>
					<li @click="changeEditStep('addTrip')">
						<div class="flex">
							<div><i class="fas fa-street-view"></i>&nbsp; Etappe</div>
							<div class="description">Legg til en manglene etappe for denne reisen</div>
						</div>
					</li>
					<li v-if="dataStore.currentJourneyIndex == 0" @click="addJourney(-1)">
						<div class="flex">
							<div><i class="fas fa-arrow-right-to-bracket"></i>&nbsp; Reise før</div>
							<div class="description">Legg til manglende reise med eget formål FØR denne</div>
						</div>
					</li>
					<li @click="addJourney(1)">
						<div class="flex">
							<div><i class="fa-solid fa-arrow-right-from-bracket"></i>&nbsp; Reise etter</div>
							<div class="description">Legg til manglende reise med eget formål ETTER denne</div>
						</div>
					</li>
				</ButtonMenu>
				<ButtonMenu color="red" full right>
					<template #button-content><i i class="fas fa-trash"></i>&nbsp;Slett</template>
					<li @click="changeEditStep('deleteTrip')">
						<div class="flex">
							<div><i class="fas fa-street-view"></i>&nbsp; Etappe</div>
							<div class="description">Slett èn etappe på denne reisen</div>
						</div>
					</li>
					<li @click="changeEditStep('deleteJourney')">
						<div class="flex">
							<div><i class="fa-solid fa-bars-staggered"></i>&nbsp; Reise</div>
							<div class="description">Slett hele denne reisen</div>
						</div>
					</li>
				</ButtonMenu>
			</div>
		</div>

		<template #footer>
			<template v-if="!dataStore.selectedDateIsToday">
				<template v-if="dataStore.currentJourney && !dataStore.currentJourney.approved && appStore.editStep == ''">
					<div class="button" @click="toggleEdit">
						<i class="fas fa-edit"></i>
						<span>Rediger</span>
					</div>
					<div class="button green full" @click="verifyJourney" v-if="canApproveCurrentJourney">
						<i class="fas fa-check"></i>
						<span>Reisen er korrekt</span>
					</div>
					<div v-if="!canApproveCurrentJourney" class="flex gap-3">
						<div class="button blue narrow" v-if="dataStore.currentJourneyIndex > 0" @click="dataStore.nextJourney(-1)">
							<i class="fas fa-chevron-left"></i>
							<span>Forrige</span>
						</div>
						<div class="button blue narrow" v-if="dataStore.currentJourneyIndex < currentJourneys.length - 1" @click="dataStore.nextJourney(1)">
							<span>Neste</span>
							<i class="fas fa-chevron-right"></i>
						</div>
					</div>
				</template>

				<template v-if="appStore.editStep == 'surveyTrip'">
					<div class="w-full">
						<div class="mb-4">
							<strong>Vennligst svar på noen korte spørsmål om noen av etappene før du fortsetter 😃</strong>
						</div>
						<div class="flex gap-6">
							<div class="button" @click="appStore.editStep = ''">
								<i class="fa-solid fa-rotate-left"></i>
								<span>Tilbake</span>
							</div>
							<div class="button green full" @click="verifyJourney" :class="{ disabled: !currentJourney.surveyTripsCompleted }">
								<i class="fas fa-save"></i>
								<span>Fortsett</span>
							</div>
						</div>
					</div>
				</template>

				<template v-if="appStore.editStep == 'change'">
					<div class="button" @click="toggleEdit">
						<i class="fa-solid fa-rotate-left"></i>
						<span>Avbryt</span>
					</div>
					<div class="button green full" @click="appStore.editStep = ''">
						<i class="fas fa-save"></i>
						<span>Fortsett</span>
					</div>
				</template>

				<template v-if="appStore.editStep == 'splitJourney'">
					<div class="w-full">
						<div class="mb-4">
							<strong>Trykk &nbsp;<i class="fas fa-cut"></i> &nbsp; for å angi hvor reisen skal deles</strong>
						</div>
						<div class="flex gap-6">
							<div class="button" @click="cancelSplitJourney()">Avbryt</div>
							<div class="button full orange" @click="splitJourney()" :class="{ disabled: splitIndex < 0 }">
								<i v-if="!currentJourney.loading" class="fas fa-up-right-and-down-left-from-center"></i>
								<LoadingSpinner v-if="currentJourney.loading"></LoadingSpinner>
								<span>Del opp reisen</span>
							</div>
						</div>
					</div>
				</template>

				<template v-if="appStore.editStep == 'joinTrips'">
					<div class="w-full">
						<div class="mb-4">
							<strong>Huk av &nbsp;<i style="color: var(--color-primary)" class="fas fa-circle-check"></i> etapper som skal slås sammen.</strong>
						</div>
						<div class="flex gap-6">
							<div class="button" :class="{ disabled: currentJourney.loading }" variant="secondary" @click="cancelJoinTrips()">Avbryt</div>
							<div class="button full orange" @click="joinTrips()" :class="{ disabled: !currentJourney.joinable }">
								<i v-if="!currentJourney.loading" class="fas fa-down-left-and-up-right-to-center"></i>
								<LoadingSpinner v-if="currentJourney.loading"></LoadingSpinner>
								<span>Slå sammen</span>
							</div>
						</div>
					</div>
				</template>

				<template v-if="dataStore.currentJourney && dataStore.currentJourney.approved">
					<div class="w-full">
						<div class="mb-3 mt-n2">
							<i class="text-success fas fa-circle-check"></i>&nbsp;
							<strong>Denne reisen er godkjent</strong>
						</div>
						<div class="flex gap-6">
							<div class="button red full" @click="clearJourneyApproval()">Nullstill&nbsp;<i class="fa-solid fa-rotate-left"></i></div>
							<div v-if="currentJourneys.length > 1 && dataStore.currentJourneyIndex == currentJourneys.length - 1" class="button green full" @click="appStore.setPage('approved')"><i class="fa-solid fa-check"></i>&nbsp;Avslutt</div>
							<div v-if="currentJourneys.length > 1 && dataStore.currentJourneyIndex < currentJourneys.length - 1" class="button blue full" @click="dataStore.nextJourney(1)">Se neste reise&nbsp;<i class="fa-solid fa-chevron-right"></i></div>
						</div>
					</div>
				</template>

				<template v-if="appStore.editStep == 'splitTrip'">
					<div class="w-full">
						<div class="mb-4">
							<strong>Velg etappe &nbsp;<i style="color: var(--color-primary)" class="fas fa-circle-check"></i> som skal deles opp.</strong>
						</div>
						<div class="flex gap-6">
							<div class="button" @click="cancelSplitTrip()">Avbryt</div>
							<div class="button full orange" @click="changeEditStep('splitTripActive')" :class="{ disabled: !currentJourney.splittable }">
								<i v-if="!currentJourney.loading" class="fas fa-down-left-and-up-right-to-center"></i>
								<LoadingSpinner v-if="currentJourney.loading"></LoadingSpinner>
								<span>Del opp etappen</span>
							</div>
						</div>
					</div>
				</template>

				<template v-if="appStore.editStep == 'addTrip'">
					<div class="w-full">
						<div class="mb-4">
							<strong>Velg etappe &nbsp;<i style="color: var(--color-primary)" class="fas fa-circle-check"></i> som skal påfølges.</strong>
						</div>
						<div class="flex gap-6">
							<div class="button" @click="cancelAddTrip()">Avbryt</div>
							<div class="button full blue" @click="addTrip()" :class="{ disabled: currentJourney.addIndex < 0 }">
								<i class="fas fa-plus"></i>
								<span>Legg til etappe</span>
							</div>
						</div>
					</div>
				</template>

				<template v-if="appStore.editStep == 'deleteTrip'">
					<div class="w-full">
						<div class="mb-4">
							<strong>Velg etappe &nbsp;<i style="color: var(--color-danger)" class="fas fa-circle-check"></i> som skal slettes.</strong>
						</div>
						<div class="flex gap-6">
							<div class="button" @click="finishDeleteTrip()">Avbryt</div>
							<div v-if="!selectedTrip?.deleted" class="button full red" @click="deleteTrip()" :class="{ disabled: currentJourney.deleteIndex < 0 }">
								<i class="fas fa-trash"></i>
								<span>Slett etappe</span>
							</div>
							<div v-if="selectedTrip?.deleted" class="button full red" @click="deleteTrip(true)" :class="{ disabled: currentJourney.deleteIndex < 0 }">
								<i class="fas fa-solid fa-trash-can-arrow-up"></i>
								<span>Angre sletting</span>
							</div>
						</div>
					</div>
				</template>

				<template v-if="appStore.editStep == 'joinNextJourney' || appStore.editStep == 'joinPrevJourney'">
					<div class="w-full">
						<div class="mb-4">
							<strong>Trykk &nbsp;<i style="color: var(--color-green)" class="fas fa-save"></i> for å bekrefte sammenslåing.</strong>
						</div>
						<div class="flex gap-6">
							<div class="button" @click="cancelJoinJourneys">
								<i class="fa-solid fa-rotate-left"></i>
								<span>Avbryt</span>
							</div>
							<div class="button green full" @click="joinJourneysClick">
								<i class="fas fa-save"></i>
								<span>Slå sammen</span>
							</div>
						</div>
					</div>
				</template>

				<template v-if="appStore.editStep == 'splitTripActive'">
					<div class="w-full">
						<div class="mb-4">
							<strong>Velg posisjonen for oppdeling ved å dra i skyveknappen.</strong>
						</div>
						<div class="splitLocationContainer mt-1">
							<input class="mt-2 w-full custom-range" @touchstart.passive="splitLocationSliderTouch(true)" @touchend.passive="splitLocationSliderTouch(false)" type="range" v-model="dataStore.currentSplitLocationRangeIndex" min="0" max="100" />
							<div class="splitLocationWrapper" :class="{ visible: appStore.showSplitLocation }" :style="{ left: 'calc(' + dataStore.currentSplitLocationRangeIndex + '% - ' + (dataStore.currentSplitLocationRangeIndex / 100) * 80 + 'px)' }">
								<div class="splitLocation" :style="{ bais: -(dataStore.currentSplitLocationRangeIndex / 3) + 'px' }">
									{{ dataStore.formatTime(dataStore.currentSplitLocation?.timestamp, true) }}
								</div>
							</div>
						</div>
						<div class="flex gap-6 mt-6">
							<div class="button" :class="{ disabled: currentJourney.loading }" @click="cancelSplitTrip()">Avbryt</div>
							<div class="button full orange" @click="performCurrentSplit()" :class="{ disabled: !currentJourney.splittable }">
								<i v-if="!currentJourney.loading" class="fas fa-down-left-and-up-right-to-center"></i>
								<div v-if="currentJourney.loading" class="animate-spin w-4 h-4 border-2 border-yellow-900 border-t-transparent rounded-full"></div>
								<span>Del {{ splitTripToJourney ? 'reisen' : 'etapper' }} her</span>
							</div>
						</div>
						<div class="mt-8 smallbox" style="background-color: #eee">
							<div class="flex items-center">
								<strong for="helper-checkbox">Dele hele reisen her?</strong>
								<ToggleSwitch class="ml-4" small v-model="splitTripToJourney"></ToggleSwitch>
							</div>
							<p id="helper-checkbox-text" class="text-gray-500 dark:text-gray-300">Dersom angitt sted er en destinasjon med eget reiseformål kan du velge at denne reisen avsluttes her, resten flyttes til ny etterfølgende reise.</p>
						</div>
					</div>
				</template>
			</template>
		</template>
	</BasePage>
</template>

<style>
.journey {
	width: 100%;
	position: relative;
	display: flex;
	flex-direction: column;
	z-index: 0;
}

.step-wrapper {
	display: flex;
	flex-direction: column;
	position: relative;
}

.step-wrapper .path {
	transition: all 0.3s ease-out;
}

.step-wrapper.splitted .path {
	opacity: 0;
	transform: translateX(-10vw);
	transition: all 0.3s ease-out 0.6s;
}

.step-wrapper.splitted .step.split {
	gap: 0px;
}

.step-wrapper.splitted .split .button {
	max-width: 0px;
	pointer-events: none;
	animation: slide-left-out 1s ease-out forwards;
}

.step-wrapper.splitted .split .line {
	animation: clip-right-left 1s ease-out forwards;
}

.step-wrapper.joined .activity,
.step-wrapper.joined .step.path {
	--icon-color: var(--color-primary) !important;
	--icon: '\f0c1' !important;
}

.step-wrapper.link .activity {
	--icon: '\f0c1' !important;
}

.step-wrapper.joined.firstJoined:not(.lastJoined) .time .arrival {
	opacity: 0.5;
	text-decoration: line-through;
}

.step-wrapper.joined:not(.firstJoined):not(.lastJoined) .time {
	opacity: 0.5;
	text-decoration: line-through;
}

.step-wrapper.joined.lastJoined:not(.firstJoined) .time .departure {
	opacity: 0.5;
	text-decoration: line-through;
}

.step-wrapper.joined:not(.lastJoined) .path {
	min-height: 0.5rem;
}

@keyframes clip-right-left {
	0% {
		clip-path: inset(0 0 0 0);
	}

	100% {
		clip-path: inset(0 100vw 0 0);
	}
}

@keyframes slide-left-out {
	0% {
		max-width: 100%;
		opacity: 1;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		transform: translateX(0%);
	}

	99% {
		max-width: 100%;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		transform: translateX(-100vw);
	}

	100% {
		max-width: 0px;
		opacity: 0;
		padding-left: 0rem;
		padding-right: 0rem;
		transform: translateX(-100vw);
	}
}

.step {
	width: 100%;
	display: flex;
	align-items: center;
	position: relative;
	/* z-index: 2; */
	gap: 0rem;
}

.step.selected {
	animation: highlight 0.5s ease-out;
}

@keyframes highlight {
	0% {
		transform: scale(1);
	}

	50% {
		background-color: rgba(255, 255, 255, 0.5);
		backdrop-filter: blur(5px);
		box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
		border-radius: 0.25rem;
	}

	100% {
		transform: scale(1);
	}
}

.hidden-below {
	opacity: 0;
	pointer-events: none;
	transform: translateY(150%);
	transition: all 0.3s ease-out;
	display: none;
}

@keyframes slide-to-hidden {
	from {
		opacity: 1;
		transform: translateY(0%);
		pointer-events: all;
	}

	to {
		opacity: 0;
		transform: translateY(150%);
		pointer-events: none;
	}
}

@keyframes slide-up-hidden {
	to {
		opacity: 1;
		transform: translateY(0%);
		pointer-events: all;
	}
}

.hidden-below.visible {
	animation: slide-up-hidden 0.5s ease-out forwards;
	display: block;
	z-index: 100;
}

.hidden-below.visible.hide {
	animation: slide-to-hidden 0.3s ease-out forwards;
}

.info strong {
	display: block;
	margin-bottom: 5px;
}

.step {
	white-space: nowrap;
	line-height: 1rem;
	gap: 0.5rem;
	position: relative;
}

.step.deleted {
	filter: grayscale(100%);
}

.step.deleted .icon {
	--icon-color: #ddd;
}

.step.deleted + .path {
	background-color: #ddd;
}

.step.deleted .info,
.step.deleted .icon .fas:before,
.edit .step.deleted .action {
	opacity: 0.5;
}

.step.deleted .info {
	text-decoration: line-through;
}

.step.deleted .metric:first-child {
	opacity: 0.3;
	text-decoration: line-through;
}

.step .info {
	font-size: 1.125rem;
	flex: 1;
	overflow: hidden;
}

.step .icon {
	position: relative;
	z-index: 2;
}

.step .path {
	position: absolute;
	width: 40px;
	height: 40px;
	transition: all 0.5s ease-out;
}

.step .path::after {
	content: '';
	position: absolute;
	bottom: -1.5rem;
	width: 6px;
	height: 1.5rem;
	background-color: var(--icon-color);
	z-index: 1;
	left: 0px;
	right: 0px;
	margin-left: auto;
	margin-right: auto;
}

.step.path {
	width: 6px;
	min-height: 1.5rem;
	background-color: var(--icon-color);
	margin-left: 17px;
	flex: 1;
}

.step.path.expanded {
	min-height: 4rem;
}

.step .action,
.step .checkbox {
	text-align: right;
	flex-grow: 1;
	pointer-events: none;
	opacity: 0;
	transform: translateX(60px);
	transition: all 0.4s ease-out;
	position: absolute;
	right: 0px;
	display: flex;
}

.step.split {
	position: absolute;
	display: flex;
	bottom: 1rem;
	align-items: center;
	justify-content: flex-end;
}

.step.survey {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
}

.step.survey .path {
	height: 100%;
}

.step.survey .content {
	padding-top: 1rem;
	flex-basis: 100%;
	gap: 1rem;
	display: flex;
	flex-direction: column;
}

.step.survey .content .question {
	display: flex;
	flex-direction: row;
	flex: 1;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
}

.step.survey .content .question .dot {
	position: relative;
	z-index: 5;
	width: 1.5rem;
	height: 1.5rem;
	margin-left: calc(40px - 2rem);
	background-color: white;
	border: 3px solid var(--icon-color);
	border-radius: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: black;
	font-size: 0.75rem;
	font-weight: 600;
	aspect-ratio: 1;
}

.step.survey .content .question .select-box {
	background: white url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"%3E%3Cpath fill="%236c757d" d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"/%3E%3C/svg%3E') right 0.75rem center/0.75rem no-repeat;
}

.step.survey .content .question .button {
	white-space: wrap;
	line-height: 1rem;
	text-align: center;
	padding-left: 1rem;
	padding-right: 1rem;
	min-height: 2rem;
	height: fit-content;
}

.step.survey .content .question .title {
	width: 100%;
}

.step.survey .content .question:last-child label {
	margin-bottom: 0;
}

.step.split .line,
.step.link .line {
	height: 1px;
	border-top: 1px dashed rgba(0, 0, 0, 0.3);
	clip-path: inset(0 0 0 0);
	flex: 1;
}

.step.split.selected .line {
	border-top: 1px dashed rgba(0, 0, 0, 0.5);
}

.edit .step .action,
.join .step .checkbox,
.select .step .checkbox,
.delete .step .checkbox,
.split .step .checkbox {
	transform: translateX(0px);
	opacity: 1;
	pointer-events: all;
}

.delete .step .checkbox .button.checked {
	background-color: var(--color-danger);
	border-color: var(--color-danger);
}

.join .step.first .checkbox {
	opacity: 0;
	pointer-events: none;
}

.step .metrics {
	display: flex;
	flex-direction: column;
	gap: 0.3125rem;
	transition: all 0.4s ease-out;
	text-align: right;
}

.edit .step .metrics,
.join .step .metrics,
.select .step .metrics,
.delete .step .metrics,
.split .step .metrics {
	transform: translateX(-60px);
	opacity: 0;
}

.step .metric,
.step .time {
	font-size: 0.9375rem;
	font-weight: 400;
}

.strike {
	text-decoration: line-through;
	text-decoration-color: var(--color-danger);
	color: rgba(0, 0, 0, 0.5);
}

.step .metric {
	opacity: 0.5;
}

.step.last {
	margin-bottom: 0;
}

.step.edited:before {
	content: ' ';
	width: 10px;
	height: 10px;
	background-color: #dc3545;
	border-radius: 100%;
	position: absolute;
	left: -18px;
	top: 0px;
	margin-top: auto;
	margin-bottom: auto;
	bottom: 0px;
}

.splitLocationContainer {
	position: relative;
	width: 100%;
}

.splitLocationContainer .custom-range {
	width: 100%;
	height: 1.4rem;
	padding: 0;
	background-color: transparent;
	appearance: none;
}

.splitLocationWrapper {
	position: absolute;
	top: -1.75rem;
	align-content: center;
	font-size: 0.88rem;
	pointer-events: none;
	text-align: left;
	background-color: var(--color-primary);
	border-radius: 1rem;
	line-height: 1.75rem;
	color: white;
	padding-left: 0.75rem;
	padding-right: 0.75rem;
	font-weight: 500;
	box-shadow:
		rgba(50, 50, 93, 0.15) 0px 13px 27px -5px,
		rgba(0, 0, 0, 0.2) 0px 8px 16px -8px;
	opacity: 0;
	transform: scale3d(0, 0, 0);
	transform-origin: bottom center;
	transition:
		transform 0.3s ease-out,
		opacity 0.3s ease-out;
}

.splitLocationWrapper.visible {
	opacity: 1;
	transform: scale3d(1, 1, 1);
}
</style>
